/**
 * `/reset-password/:token` view — issue #117.
 *
 * Two password inputs + confirm. The token comes from the URL; the user
 * never sees / pastes it directly. On success we render a "password
 * updated" panel + a link to /sign-in.
 */
import { button, card, textInput } from '@dl/ui';
import { div, form, h1, label, main, p, span, text, type Mountable, type Send, type Signal } from '@llui/dom';

import { t } from '../i18n.js';

import { confirmPasswordReset } from './api.js';
import {
  clearFormErrors,
  focusFirstError,
  renderAuthSuccess,
  setFieldError,
  setFormError,
  setSubmitting,
} from './forms.js';

const MIN_PASSWORD_LENGTH = 12;

async function onSubmit(event: SubmitEvent, token: string): Promise<void> {
  event.preventDefault();
  const formEl = event.currentTarget as HTMLFormElement;
  clearFormErrors(formEl);
  const data = new FormData(formEl);
  const password = String(data.get('password') ?? '');
  const confirm = String(data.get('confirm') ?? '');
  let hasError = false;
  if (password.length < MIN_PASSWORD_LENGTH) {
    setFieldError(formEl, 'reset-password', t('auth.reset.error_short'));
    hasError = true;
  }
  if (password !== confirm) {
    setFieldError(formEl, 'reset-confirm', t('auth.reset.error_mismatch'));
    hasError = true;
  }
  if (hasError) {
    focusFirstError(formEl);
    return;
  }
  setSubmitting(formEl, true);
  const result = await confirmPasswordReset({ token, new_password: password });
  setSubmitting(formEl, false);
  if (result.ok) {
    const card = formEl.closest<HTMLElement>('.auth-card');
    if (card) {
      renderAuthSuccess(card, {
        heading: t('auth.reset.heading'),
        body: t('auth.reset.success'),
        bodyTestid: 'reset-password-success',
        linkHref: '/sign-in',
        linkText: t('auth.reset.continue_link'),
        linkTestid: 'reset-password-continue',
      });
    }
    return;
  }
  if (result.status === 400) {
    setFormError(formEl, t('auth.reset.error_invalid_token'));
    return;
  }
  setFormError(formEl, result.error);
}

export function viewResetPassword<Model, Msg>(
  _state: Signal<Model>,
  _send: Send<Msg>,
  token: string,
): Mountable {
  const handleSubmit = (event: Event): void => {
    void onSubmit(event as SubmitEvent, token);
  };
  return main({ role: 'main', class: 'auth-shell', 'data-testid': 'reset-password-view' }, [
    card({ variant: 'default', padding: 'xl', className: 'auth-card', children: [
      h1([text(t('auth.reset.heading'))]),
      form(
        {
          class: 'auth-form',
          'data-testid': 'reset-password-form',
          novalidate: true,
          onSubmit: handleSubmit,
        },
        [
          div({ class: 'auth-form-row' }, [
            label({ for: 'reset-password' }, [text(t('auth.reset.password_label'))]),
            textInput({
              type: 'password',
              testid: 'reset-password-input',
              attrs: {
                id: 'reset-password',
                name: 'password',
                autocomplete: 'new-password',
                minlength: MIN_PASSWORD_LENGTH,
                required: true,
                'aria-describedby': 'reset-password-error',
              },
            }),
            span(
              {
                id: 'reset-password-error',
                'data-error-for': 'reset-password',
                class: 'auth-field-error',
                role: 'alert',
              },
              [],
            ),
          ]),
          div({ class: 'auth-form-row' }, [
            label({ for: 'reset-confirm' }, [text(t('auth.reset.confirm_label'))]),
            textInput({
              type: 'password',
              testid: 'reset-password-confirm',
              attrs: {
                id: 'reset-confirm',
                name: 'confirm',
                autocomplete: 'new-password',
                required: true,
                'aria-describedby': 'reset-confirm-error',
              },
            }),
            span(
              {
                id: 'reset-confirm-error',
                'data-error-for': 'reset-confirm',
                class: 'auth-field-error',
                role: 'alert',
              },
              [],
            ),
          ]),
          p({ class: 'auth-form-error', 'data-form-error': '', role: 'alert' }, []),
          div({ class: 'auth-form-actions' }, [
            button({
              variant: 'primary',
              type: 'submit',
              testid: 'reset-password-submit',
              children: [text(t('auth.reset.submit'))],
            }),
          ]),
        ],
      ),
    ] }),
  ]);
}
